import axios from 'axios';
import createSignedRequest from './createSignedRequest';
import { toggleAlertMessage } from '../action.js';

const { apiURL } = require('./apiURL');

const BASE = '/my/connections';

export const Types = {
  CONN_SET_LIST: 'CONN_SET_LIST',
  CONN_SET_CATALOG: 'CONN_SET_CATALOG',
  CONN_SET_DESCRIPTOR: 'CONN_SET_DESCRIPTOR',
  CONN_SET_EXTERNAL_ACCOUNTS: 'CONN_SET_EXTERNAL_ACCOUNTS',
  CONN_SET_DIMENSION_MANIFEST: 'CONN_SET_DIMENSION_MANIFEST',
  CONN_SET_DIMENSION_VALUES: 'CONN_SET_DIMENSION_VALUES',
  CONN_SET_MAPPING_CONFIG: 'CONN_SET_MAPPING_CONFIG',
  CONN_SET_IMPORT_SUMMARY: 'CONN_SET_IMPORT_SUMMARY',
  CONN_SET_LOADING: 'CONN_SET_LOADING',
  CONN_SET_ERROR: 'CONN_SET_ERROR',
  CONN_CLEAR_DESCRIPTOR: 'CONN_CLEAR_DESCRIPTOR',
};

const setList = (payload) => ({ type: Types.CONN_SET_LIST, payload });
const setCatalog = (payload) => ({ type: Types.CONN_SET_CATALOG, payload });
const setDescriptor = (payload) => ({ type: Types.CONN_SET_DESCRIPTOR, payload });
const setExternalAccounts = (payload) => ({ type: Types.CONN_SET_EXTERNAL_ACCOUNTS, payload });
const setDimensionManifest = (payload) => ({ type: Types.CONN_SET_DIMENSION_MANIFEST, payload });
const setDimensionValues = (payload) => ({ type: Types.CONN_SET_DIMENSION_VALUES, payload });
const setMappingConfig = (payload) => ({ type: Types.CONN_SET_MAPPING_CONFIG, payload });
const setImportSummary = (payload) => ({ type: Types.CONN_SET_IMPORT_SUMMARY, payload });
const setLoading = (section, value) => ({ type: Types.CONN_SET_LOADING, payload: { section, value } });
const setError = (payload) => ({ type: Types.CONN_SET_ERROR, payload });
const clearDescriptor = () => ({ type: Types.CONN_CLEAR_DESCRIPTOR });

const orgHeader = (state) => ({ Organisation: String(state.currentOrganisation) });

/**
 * Resolve a path that may be absolute (https) or API-relative (/my/...).
 * @param {string} path
 * @returns {string}
 */
const resolveApiUrl = (path) => {
  if (!path) return apiURL + BASE;
  if (/^https?:\/\//i.test(path)) return path;
  return apiURL + (path.startsWith('/') ? path : `/${path}`);
};

const getErrorPayload = (e) => {
  const data = e?.response?.data;
  return {
    message: data?.error || e?.message || 'Request failed',
    code: data?.code || null,
    status: e?.response?.status,
    data,
  };
};

/**
 * List connection instances for the current organisation.
 */
export const listConnections = () => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('list', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + BASE, '', orgHeader(state)),
    );
    dispatch(setList(response.data?.connections || []));
    dispatch(setError(null));
  } catch (e) {
    dispatch(setError(getErrorPayload(e)));
  } finally {
    dispatch(setLoading('list', false));
  }
};

/**
 * Fetch connectable catalog (logos, connectable flags).
 */
export const getConnectionCatalog = () => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('catalog', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + BASE + '/catalog', '', orgHeader(state)),
    );
    dispatch(setCatalog(response.data?.connectors || []));
  } catch (e) {
    dispatch(setError(getErrorPayload(e)));
  } finally {
    dispatch(setLoading('catalog', false));
  }
};

/**
 * Load full connector descriptor (auth, setupSteps, ingest, logos).
 * @param {string} connectorId
 */
export const getConnectionDescriptor = (connectorId) => async (dispatch, getState) => {
  const state = getState();
  if (!connectorId) {
    dispatch(setError({ message: 'Missing connector id', code: 'MISSING_CONNECTOR_ID' }));
    return null;
  }
  dispatch(setLoading('descriptor', true));
  dispatch(setError(null));
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + `${BASE}/${encodeURIComponent(connectorId)}`, '', orgHeader(state)),
    );
    dispatch(setDescriptor(response.data));
    dispatch(setError(null));
    return response.data;
  } catch (e) {
    const err = getErrorPayload(e);
    dispatch(setError(err));
    dispatch(setDescriptor(null));
    return null;
  } finally {
    dispatch(setLoading('descriptor', false));
  }
};

/**
 * Start OAuth (or reconnect). Redirects browser to authorizeUrl.
 * @param {string} connectorId
 * @param {{ reconnect?: boolean }} [options]
 */
export const startConnectionConnect = (connectorId, options = {}) => async (dispatch, getState) => {
  const state = getState();
  const reconnect = Boolean(options.reconnect);
  const orgId = state.currentOrganisation;
  if (orgId) {
    // Persist so post-OAuth full-page reload restores the same org header used for connect.
    localStorage.setItem('organisation', String(orgId));
    localStorage.setItem('Organisation', String(orgId));
  }
  dispatch(setLoading('connect', true));
  try {
    const path = reconnect ? `${BASE}/${connectorId}/reconnect` : `${BASE}/${connectorId}/connect`;
    const response = await axios(
      await createSignedRequest('POST', apiURL + path, JSON.stringify({}), orgHeader(state)),
    );
    const { authorizeUrl } = response.data || {};
    if (!authorizeUrl) {
      throw new Error('No authorizeUrl returned from connect');
    }
    window.location.href = authorizeUrl;
    return response.data;
  } catch (e) {
    const err = getErrorPayload(e);
    dispatch(setError(err));
    dispatch(toggleAlertMessage(true, err.message, 'error'));
    return null;
  } finally {
    dispatch(setLoading('connect', false));
  }
};

/**
 * Disconnect connector; mappingConfig is kept server-side.
 * @param {string} connectorId
 */
export const disconnectConnection = (connectorId) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('disconnect', true));
  try {
    await axios(
      await createSignedRequest('POST', apiURL + `${BASE}/${connectorId}/disconnect`, JSON.stringify({}), orgHeader(state)),
    );
    dispatch(toggleAlertMessage(true, 'Disconnected successfully', 'success'));
    await dispatch(getConnectionDescriptor(connectorId));
    await dispatch(listConnections());
    return true;
  } catch (e) {
    const err = getErrorPayload(e);
    dispatch(setError(err));
    dispatch(toggleAlertMessage(true, err.message, 'error'));
    return false;
  } finally {
    dispatch(setLoading('disconnect', false));
  }
};

/**
 * Load external accounts for a setup step (or connector).
 * @param {string} connectorId
 * @param {string} [optionsUrl] - from setupSteps[].optionsUrl
 */
export const getExternalAccounts = (connectorId, optionsUrl) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('externalAccounts', true));
  try {
    const url = resolveApiUrl(optionsUrl || `${BASE}/${connectorId}/external-accounts`);
    const response = await axios(await createSignedRequest('GET', url, '', orgHeader(state)));
    dispatch(setExternalAccounts(response.data));
    return response.data;
  } catch (e) {
    dispatch(setError(getErrorPayload(e)));
    return null;
  } finally {
    dispatch(setLoading('externalAccounts', false));
  }
};

/**
 * Select an external account / organisation.
 * The POST returns only the new `selectedId`, so the cached accounts payload is patched
 * before refetching — otherwise the picker renders the previous selection until the GET lands.
 * @param {string} connectorId
 * @param {{ id: string, label?: string }} account
 * @param {string} [submitUrl]
 */
export const selectExternalAccount = (connectorId, account, submitUrl) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('externalAccounts', true));
  try {
    const url = resolveApiUrl(submitUrl || `${BASE}/${connectorId}/external-accounts`);
    const response = await axios(
      await createSignedRequest('POST', url, JSON.stringify({ id: account.id, label: account.label }), orgHeader(state)),
    );

    const cached = getState().connections?.externalAccounts;
    if (cached) {
      dispatch(
        setExternalAccounts({
          ...cached,
          selectedId: response.data?.selectedId || account.id,
          externalTenantName: response.data?.externalTenantName || account.label || cached.externalTenantName,
          needsSelection: false,
        }),
      );
    }

    dispatch(toggleAlertMessage(true, 'Organisation selected', 'success'));
    await dispatch(getExternalAccounts(connectorId));
    await dispatch(getConnectionDescriptor(connectorId));
    await dispatch(listConnections());
    return response.data;
  } catch (e) {
    const err = getErrorPayload(e);
    dispatch(setError(err));
    dispatch(toggleAlertMessage(true, err.message, 'error'));
    return null;
  } finally {
    dispatch(setLoading('externalAccounts', false));
  }
};

/**
 * @param {string} connectorId
 */
export const getDimensionManifest = (connectorId) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('manifest', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + `${BASE}/${connectorId}/dimension-manifest`, '', orgHeader(state)),
    );
    dispatch(setDimensionManifest(response.data));
    return response.data;
  } catch (e) {
    dispatch(setError(getErrorPayload(e)));
    return null;
  } finally {
    dispatch(setLoading('manifest', false));
  }
};

/**
 * @param {string} connectorId
 * @param {string} type
 * @param {string} [platformSlot]
 */
export const getDimensionValues = (connectorId, type, platformSlot) => async (dispatch, getState) => {
  const state = getState();
  const key = platformSlot ? `${type}:${platformSlot}` : type;
  dispatch(setLoading('dimensionValues', true));
  try {
    let path = `${BASE}/${connectorId}/dimension-values?type=${encodeURIComponent(type)}`;
    if (platformSlot) path += `&platformSlot=${encodeURIComponent(platformSlot)}`;
    const response = await axios(await createSignedRequest('GET', apiURL + path, '', orgHeader(state)));
    dispatch(setDimensionValues({ key, values: response.data?.values || [] }));
    return response.data?.values || [];
  } catch (e) {
    dispatch(setError(getErrorPayload(e)));
    return [];
  } finally {
    dispatch(setLoading('dimensionValues', false));
  }
};

/**
 * @param {string} connectorId
 */
export const getMappingConfig = (connectorId) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('mapping', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + `${BASE}/${connectorId}/mapping-config`, '', orgHeader(state)),
    );
    dispatch(setMappingConfig(response.data));
    return response.data;
  } catch (e) {
    dispatch(setError(getErrorPayload(e)));
    return null;
  } finally {
    dispatch(setLoading('mapping', false));
  }
};

/**
 * @param {string} connectorId
 * @param {object} mappingConfig
 */
export const putMappingConfig = (connectorId, mappingConfig) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('mapping', true));
  try {
    const response = await axios(
      await createSignedRequest(
        'PUT',
        apiURL + `${BASE}/${connectorId}/mapping-config`,
        JSON.stringify({ mappingConfig }),
        orgHeader(state),
      ),
    );
    dispatch(setMappingConfig({ ...response.data, mappingConfigDraft: null }));
    dispatch(toggleAlertMessage(true, 'Mapping saved', 'success'));
    return response.data;
  } catch (e) {
    const err = getErrorPayload(e);
    dispatch(setError(err));
    dispatch(toggleAlertMessage(true, err.message, 'error'));
    return null;
  } finally {
    dispatch(setLoading('mapping', false));
  }
};

/**
 * Build sync request body from a year or inclusive date range.
 * @param {{ year?: number, from?: string, to?: string }} window
 * @returns {{ year: number } | { from: string, to: string }}
 */
const buildSyncBody = (window) => {
  if (window?.from && window?.to) {
    return { from: window.from, to: window.to };
  }
  if (window?.year != null) {
    return { year: Number(window.year) };
  }
  throw new Error('Sync requires either year or from+to');
};

/**
 * Start spend sync via descriptor.ingest.path (always staging).
 * Accepts either `{ year }` or `{ from, to }` (ISO dates, inclusive).
 * @param {string} connectorId
 * @param {{ year?: number, from?: string, to?: string }} window
 * @param {{ method?: string, path?: string, bodySchema?: object }} [ingest]
 * @returns {Promise<object>} sync response (`path`, `import_job_id`, or `no_data`)
 * @example
 * syncConnection('xero', { year: 2025 }, descriptor.ingest)
 * syncConnection('xero', { from: '2025-08-01', to: '2025-08-07' }, descriptor.ingest)
 */
export const syncConnection = (connectorId, window, ingest) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('sync', true));
  try {
    const method = (ingest?.method || 'POST').toUpperCase();
    const path = ingest?.path || `${BASE}/${connectorId}/sync`;
    const url = resolveApiUrl(path);
    const body = buildSyncBody(window);
    const response = await axios(
      await createSignedRequest(method, url, JSON.stringify(body), orgHeader(state)),
    );
    return response.data;
  } catch (e) {
    const err = getErrorPayload(e);
    dispatch(setError(err));
    if (err.code !== 'NEEDS_EXTERNAL_ORGANISATION' && err.status !== 409) {
      dispatch(toggleAlertMessage(true, err.message, 'error'));
    }
    throw err;
  } finally {
    dispatch(setLoading('sync', false));
  }
};

/**
 * Org-level imports needing review (sidebar badge / hub).
 */
export const getImportSummary = () => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('summary', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + '/my/intelligent-imports/summary', '', orgHeader(state)),
    );
    dispatch(setImportSummary(response.data || { needingReview: 0, jobIds: [], incompleteJobs: [], incomplete: 0 }));
    return response.data;
  } catch (e) {
    dispatch(setImportSummary({ needingReview: 0, jobIds: [], incompleteJobs: [], incomplete: 0 }));
    return null;
  } finally {
    dispatch(setLoading('summary', false));
  }
};

export const Actions = {
  listConnections,
  getConnectionCatalog,
  getConnectionDescriptor,
  startConnectionConnect,
  disconnectConnection,
  getExternalAccounts,
  selectExternalAccount,
  getDimensionManifest,
  getDimensionValues,
  getMappingConfig,
  putMappingConfig,
  syncConnection,
  getImportSummary,
  clearDescriptor,
  setError,
};
